import { useState, useEffect, useRef } from "react";
import { X, ArrowRight, ArrowLeft, Check, Info, Sparkles, ShieldCheck, Upload, FileText, CreditCard, MapPin, Loader2, Lock } from "lucide-react";
import { submitParticulierLead, uploadParticulierDocuments } from "@/lib/api";

/**
 * AidesPACWizard — Questionnaire complet PAC / MaPrimeRénov' / CEE
 * Partenariat ECONATION × Rénobat.
 * Étapes conditionnelles (combles/sous-sol si "Oui", adresse fiscale si "Non").
 */
export default function AidesPACWizard({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const initial = {
    first_name: "", last_name: "", phone: "", email: "",
    work_address: "", postal_code: "", city: "",
    fiscal_address_match: "", fiscal_address: "",
    owner: "",
    surface_m2: "", construction_year: "",
    current_heating: "",
    radiator_count: "", radiator_type: "",
    underfloor_heating: "",
    water_heating: "",
    has_attic: "", attic_insulated: "", attic_insulation_year: "", attic_one_euro_offer: "",
    has_basement: "", basement_insulated: "", basement_insulation_year: "", basement_one_euro_offer: "",
    electrical_system: "", is_insulated: "",
    household_size: "", household_composition: "", fiscal_income: "",
    mpr_account: "", mpr_identifier: "",
    gdpr_consent: false,
  };
  const [form, setForm] = useState(initial);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(0); setSubmitted(false); setError(""); setResult(null); setForm(initial);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* ------------------- STEPS DEFINITION ------------------- */
  const buildSteps = () => {
    const s = [];

    // 0 · Intro
    s.push({ id: "intro", type: "intro" });

    // 1 · Identité (nom, prénom)
    s.push({ id: "identity", type: "identity" });

    // 2 · Téléphone + email
    s.push({ id: "contact", type: "contact" });

    // 3 · Adresse des travaux
    s.push({ id: "work_address", type: "address" });

    // 4 · Adresse fiscale ?
    s.push({
      id: "fiscal_address_match",
      type: "choice",
      title: "Cette adresse figure-t-elle sur votre avis d'imposition ?",
      hint: "MaPrimeRénov' vérifie la correspondance avec votre déclaration fiscale.",
      options: [{ v: "yes", l: "Oui, c'est la même" }, { v: "no", l: "Non, différente" }],
    });

    // 4b · Si non, adresse fiscale
    if (form.fiscal_address_match === "no") {
      s.push({
        id: "fiscal_address",
        type: "text",
        title: "Quelle est l'adresse figurant sur votre avis d'imposition ?",
        placeholder: "Ex : 8 avenue de la République, 92100 Boulogne",
        pattern: /^.{5,}$/,
      });
    }

    // 5 · Propriétaire
    s.push({
      id: "owner", type: "choice",
      title: "Êtes-vous propriétaire du logement ?",
      hint: "Les aides PAC sont réservées aux propriétaires (occupants ou bailleurs).",
      options: [{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }],
    });

    // 6 · Surface
    s.push({
      id: "surface_m2", type: "number",
      title: "Quelle est la surface habitable du logement ?",
      placeholder: "Ex : 110", suffix: "m²", min: 10, max: 2000,
    });

    // 7 · Année construction
    s.push({
      id: "construction_year", type: "number",
      title: "En quelle année le logement a-t-il été construit ?",
      placeholder: "Ex : 1985", min: 1800, max: new Date().getFullYear(),
      inputMode: "numeric", maxLength: 4,
    });

    // 8 · Chauffage actuel
    s.push({
      id: "current_heating", type: "choice", grid: true,
      title: "Quel est votre mode de chauffage actuel ?",
      options: [
        { v: "fioul", l: "Fioul" }, { v: "gaz", l: "Gaz" },
        { v: "electrique", l: "Électrique" }, { v: "bois", l: "Bois" },
        { v: "pac", l: "Pompe à chaleur" }, { v: "autre", l: "Autre" },
      ],
    });

    // 9 · Radiateurs
    s.push({
      id: "radiator_count", type: "number",
      title: "Combien avez-vous de radiateurs ?",
      placeholder: "Ex : 8", min: 0, max: 100, suffix: "rad.",
    });

    // 10 · Type radiateurs
    s.push({
      id: "radiator_type", type: "choice", grid: true,
      title: "En quoi sont vos radiateurs ?",
      options: [
        { v: "fonte", l: "Fonte" }, { v: "acier", l: "Acier" },
        { v: "aluminium", l: "Aluminium" }, { v: "autre", l: "Autre / je ne sais pas" },
      ],
    });

    // 11 · Plancher chauffant
    s.push({
      id: "underfloor_heating", type: "choice",
      title: "Avez-vous un plancher chauffant ?",
      options: [{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }],
    });

    // 12 · ECS
    s.push({
      id: "water_heating", type: "choice", grid: true,
      title: "Comment produisez-vous votre eau chaude sanitaire ?",
      options: [
        { v: "ballon_electrique", l: "Ballon électrique" },
        { v: "chaudiere", l: "Chaudière" },
        { v: "autre", l: "Autre / je ne sais pas" },
      ],
    });

    // 13 · Combles
    s.push({
      id: "has_attic", type: "choice",
      title: "Votre logement dispose-t-il de combles ?",
      options: [{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }],
    });
    if (form.has_attic === "yes") {
      s.push({ id: "attic_details", type: "attic_details" });
    }

    // 14 · Sous-sol
    s.push({
      id: "has_basement", type: "choice",
      title: "Votre logement dispose-t-il d'un sous-sol ?",
      options: [{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }],
    });
    if (form.has_basement === "yes") {
      s.push({ id: "basement_details", type: "basement_details" });
    }

    // 15 · Électrique
    s.push({
      id: "electrical_system", type: "choice", grid: true,
      title: "Votre installation électrique est en :",
      options: [
        { v: "monophase", l: "Monophasé" },
        { v: "triphase", l: "Triphasé" },
        { v: "unknown", l: "Je ne sais pas" },
      ],
    });

    // 15b · Isolation globale du logement
    s.push({
      id: "is_insulated", type: "choice", grid: true,
      title: "Votre maison est-elle isolée ?",
      hint: "Une bonne isolation optimise le rendement de votre future pompe à chaleur.",
      options: [
        { v: "fully", l: "Oui, entièrement" },
        { v: "partially", l: "Partiellement" },
        { v: "none", l: "Non" },
        { v: "unknown", l: "Je ne sais pas" },
      ],
    });

    // 16 · Foyer
    s.push({
      id: "household_size", type: "number",
      title: "Combien de personnes composent votre foyer ?",
      placeholder: "Ex : 4", min: 1, max: 20, suffix: "pers.",
    });

    // 16b · Composition foyer (déterminera les CNI à demander)
    s.push({
      id: "household_composition", type: "choice", grid: true,
      title: "Quelle est la composition de votre foyer ?",
      hint: "Nous adaptons les pièces d'identité demandées à votre situation.",
      options: [
        { v: "single", l: "Célibataire" },
        { v: "couple", l: "Couple" },
        { v: "family", l: "Famille avec enfants" },
      ],
    });

    // 17 · Revenu fiscal
    s.push({
      id: "fiscal_income", type: "number",
      title: "Quel est votre revenu fiscal de référence ?",
      hint: (
        <span>
          Vous le trouverez sur votre dernier <strong>avis d&apos;imposition</strong>,
          page 1, cadre « Revenu fiscal de référence ».
        </span>
      ),
      placeholder: "Ex : 42 000", min: 0, max: 10000000, suffix: "€",
    });

    // 18 · MPR compte
    s.push({
      id: "mpr_account", type: "choice",
      title: "Avez-vous déjà créé votre compte MaPrimeRénov' ?",
      options: [
        { v: "yes", l: "Oui, j'ai un compte" },
        { v: "no", l: "Non, à créer" },
        { v: "unknown", l: "Je ne sais pas" },
      ],
    });
    if (form.mpr_account === "yes") {
      s.push({
        id: "mpr_identifier", type: "text",
        title: "Quel est l'identifiant (email) de votre compte MaPrimeRénov' ?",
        hint: (
          <span>
            <ShieldCheck className="inline h-3.5 w-3.5 mr-1 text-[#10B981]" />
            <strong>Aucun mot de passe demandé.</strong> Notre conseiller vous accompagnera
            à la connexion lors du rappel — vous saisirez vous-même votre code.
          </span>
        ),
        placeholder: "email@exemple.fr",
        pattern: /^\S+@\S+\.\S+$/,
      });
    }

    // 19 · Consentement + envoi
    s.push({ id: "consent", type: "consent" });

    return s;
  };

  const steps = buildSteps();
  const total = steps.length;
  const current = steps[Math.min(step, total - 1)];
  const progress = Math.round(((step + 1) / total) * 100);

  const isStepValid = () => {
    const c = current;
    if (c.type === "intro") return true;
    if (c.type === "identity") return form.first_name.trim() && form.last_name.trim();
    if (c.type === "contact") {
      const emailOk = /^\S+@\S+\.\S+$/.test(form.email);
      const phoneOk = form.phone.replace(/\D/g, "").length >= 8;
      return emailOk && phoneOk;
    }
    if (c.type === "address") {
      return form.work_address.trim().length >= 5 && /^\d{5}$/.test(form.postal_code) && form.city.trim();
    }
    if (c.type === "choice") return !!form[c.id];
    if (c.type === "text") {
      const v = form[c.id] || "";
      return c.pattern ? c.pattern.test(v) : v.trim().length > 0;
    }
    if (c.type === "number") {
      const n = Number(form[c.id]);
      if (!Number.isFinite(n)) return false;
      if (c.min !== undefined && n < c.min) return false;
      if (c.max !== undefined && n > c.max) return false;
      return true;
    }
    if (c.type === "attic_details") {
      if (!form.attic_insulated) return false;
      if (form.attic_insulated === "yes") return !!form.attic_insulation_year && !!form.attic_one_euro_offer;
      return true;
    }
    if (c.type === "basement_details") {
      if (!form.basement_insulated) return false;
      if (form.basement_insulated === "yes") return !!form.basement_insulation_year && !!form.basement_one_euro_offer;
      return true;
    }
    if (c.type === "consent") return form.gdpr_consent === true;
    return true;
  };

  const next = () => {
    if (!isStepValid()) { setError("Merci de compléter cette étape avant de continuer."); return; }
    setError("");
    if (step < total - 1) setStep(step + 1);
    else submit();
  };
  const prev = () => { setError(""); if (step > 0) setStep(step - 1); };

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        work_address: form.work_address.trim(),
        postal_code: form.postal_code,
        city: form.city.trim(),
        fiscal_address_match: form.fiscal_address_match,
        fiscal_address: form.fiscal_address_match === "no" ? form.fiscal_address.trim() : null,
        owner: form.owner,
        primary_residence: "yes",
        housing_type: "maison",
        surface_m2: Number(form.surface_m2),
        construction_year: Number(form.construction_year),
        current_heating: form.current_heating,
        radiator_count: form.radiator_count === "" ? null : Number(form.radiator_count),
        radiator_type: form.radiator_type || null,
        underfloor_heating: form.underfloor_heating || "no",
        water_heating: form.water_heating || "chaudiere",
        has_attic: form.has_attic,
        attic_insulated: form.has_attic === "yes" ? form.attic_insulated : null,
        attic_insulation_year: form.has_attic === "yes" && form.attic_insulated === "yes"
          ? Number(form.attic_insulation_year) : null,
        attic_one_euro_offer: form.has_attic === "yes" && form.attic_insulated === "yes"
          ? form.attic_one_euro_offer : null,
        has_basement: form.has_basement,
        basement_insulated: form.has_basement === "yes" ? form.basement_insulated : null,
        basement_insulation_year: form.has_basement === "yes" && form.basement_insulated === "yes"
          ? Number(form.basement_insulation_year) : null,
        basement_one_euro_offer: form.has_basement === "yes" && form.basement_insulated === "yes"
          ? form.basement_one_euro_offer : null,
        electrical_system: form.electrical_system || "unknown",
        is_insulated: form.is_insulated || null,
        household_size: Number(form.household_size),
        household_composition: form.household_composition || null,
        fiscal_income: Number(form.fiscal_income),
        replace_heating: "yes",
        mpr_account: form.mpr_account || "unknown",
        mpr_identifier: form.mpr_account === "yes" ? form.mpr_identifier.trim() : null,
        gdpr_consent: form.gdpr_consent,
      };
      const res = await submitParticulierLead(payload);
      setResult(res);
      setSubmitted(true);
    } catch (e) {
      setError("Une erreur est survenue. Merci de réessayer dans un instant.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputRef = useRef(null);
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open, step]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !submitted && current?.type !== "consent") {
      const target = e.target;
      if (target && target.tagName === "TEXTAREA") return;
      e.preventDefault(); next();
    }
    if (e.key === "Escape") onClose();
  };

  if (!open) return null;

  return (
    <div
      data-testid="pac-wizard-modal"
      className="fixed inset-0 z-[9999] flex flex-col bg-[#0F172A]"
      role="dialog"
      aria-modal="true"
      onKeyDown={onKeyDown}
    >
      {/* Top bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-10 py-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#0055A4] to-[#003D7A] grid place-items-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-white text-sm font-semibold tracking-tight">Aides Pompe à chaleur</div>
            <div className="text-white/50 text-[11px] uppercase tracking-[0.22em]">ECONATION × Rénobat</div>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="pac-wizard-close"
          aria-label="Fermer"
          className="h-10 w-10 rounded-full grid place-items-center bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 hover:text-white transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress */}
      {!submitted && (
        <div className="flex-shrink-0 px-5 sm:px-10 py-4">
          <div className="flex items-center justify-between text-[11px] text-white/60 mb-2">
            <span className="uppercase tracking-[0.22em] font-semibold">Étape {step + 1} / {total}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#0055A4] to-[#003D7A] transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="pac-wizard-progress"
            />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-5 sm:px-10 py-10 sm:py-14">
          {submitted ? (
            <SuccessScreen firstName={form.first_name} result={result} householdComposition={form.household_composition} onClose={onClose} />
          ) : (
            <StepView
              key={step}
              step={current}
              form={form}
              set={set}
              setError={setError}
              autoAdvance={() => setTimeout(() => setStep((s) => Math.min(s + 1, total - 1)), 180)}
              inputRef={inputRef}
            />
          )}
          {!submitted && error && (
            <div className="mt-6 text-sm text-[#FCA5A5]" data-testid="pac-wizard-error">{error}</div>
          )}
        </div>
      </div>

      {/* Footer nav */}
      {!submitted && (
        <div className="flex-shrink-0 border-t border-white/5 px-5 sm:px-10 py-5">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <button
              onClick={prev}
              disabled={step === 0}
              data-testid="pac-wizard-back"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 border border-white/10 px-5 py-3 text-white text-xs font-semibold tracking-wide uppercase transition"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Retour
            </button>
            <button
              onClick={next}
              disabled={submitting}
              data-testid="pac-wizard-next"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#0055A4] to-[#003D7A] hover:from-[#003D7A] hover:to-[#0055A4] disabled:opacity-60 px-8 py-3.5 text-white text-sm font-semibold tracking-wide transition shadow-lg shadow-[#0055A4]/30 hover:-translate-y-0.5"
            >
              {submitting ? "Envoi..." : step === total - 1 ? "Envoyer ma demande" : "Continuer"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

/* ================= STEP RENDERERS ================= */

function StepView({ step, form, set, setError, autoAdvance, inputRef }) {
  return (
    <div className="animate-[fadein_.5s_ease-out]">
      {step.type !== "intro" && step.title && (
        <>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-3">
            {step.title}
          </h2>
          {step.hint ? (
            <p className="flex items-start gap-2 text-white/60 text-sm leading-relaxed mb-8 sm:mb-10">
              <Info className="h-4 w-4 mt-0.5 text-[#0055A4] flex-shrink-0" />
              <span>{step.hint}</span>
            </p>
          ) : <div className="h-6" />}
        </>
      )}

      {step.type === "intro" && <IntroStep />}

      {step.type === "identity" && <IdentityStep form={form} set={set} setError={setError} inputRef={inputRef} />}

      {step.type === "contact" && <ContactStep form={form} set={set} setError={setError} inputRef={inputRef} />}

      {step.type === "address" && <AddressStep form={form} set={set} setError={setError} inputRef={inputRef} />}

      {step.type === "choice" && (
        <div className={`grid gap-3 ${step.grid ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
          {step.options.map((opt) => (
            <button
              key={opt.v}
              data-testid={`pac-option-${step.id}-${opt.v}`}
              onClick={() => { set(step.id, opt.v); setError(""); autoAdvance(); }}
              className={`group relative rounded-2xl border-2 p-5 sm:p-6 text-left transition-all hover:-translate-y-0.5 ${
                form[step.id] === opt.v
                  ? "border-[#0055A4] bg-[#0055A4]/10 text-white"
                  : "border-white/10 bg-white/[0.03] text-white/85 hover:border-white/25 hover:bg-white/[0.06]"
              }`}
            >
              <div className="text-base sm:text-lg font-semibold">{opt.l}</div>
              {form[step.id] === opt.v && (
                <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-[#0055A4] grid place-items-center">
                  <Check className="h-3.5 w-3.5 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {(step.type === "text" || step.type === "number") && (
        <div className="relative">
          <input
            ref={inputRef}
            data-testid={`pac-input-${step.id}`}
            inputMode={step.inputMode || (step.type === "number" ? "numeric" : "text")}
            placeholder={step.placeholder}
            value={form[step.id]}
            maxLength={step.maxLength}
            onChange={(e) => {
              let v = e.target.value;
              if (step.type === "number") v = v.replace(/[^\d]/g, "");
              set(step.id, v);
              setError("");
            }}
            className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-xl sm:text-2xl font-semibold text-white placeholder:text-white/25 outline-none transition"
          />
          {step.suffix && <span className="absolute right-6 top-1/2 -translate-y-1/2 text-white/40 text-lg font-medium">{step.suffix}</span>}
        </div>
      )}

      {step.type === "attic_details" && <AtticDetails form={form} set={set} setError={setError} />}
      {step.type === "basement_details" && <BasementDetails form={form} set={set} setError={setError} />}
      {step.type === "consent" && <ConsentStep form={form} set={set} />}
    </div>
  );
}

function IntroStep() {
  return (
    <div className="animate-[fadein_.6s_ease-out]">
      <div className="inline-flex items-center gap-2 rounded-full border border-[#EAB308]/25 bg-[#EAB308]/[0.06] px-3 py-1.5 mb-6">
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#EAB308] font-bold">ECONATION × Rénobat</span>
      </div>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-6">
        Vérifions votre éligibilité aux aides CEE et MaPrimeRénov&apos;.
      </h2>
      <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
        En partenariat avec <strong className="text-white">Rénobat</strong>, ECONATION vous accompagne
        de A à Z : de la vérification d&apos;éligibilité jusqu&apos;à la mise en service de votre
        pompe à chaleur, avec zéro avance sur les aides éligibles.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
        {[
          "Environ 5 minutes",
          "Sans engagement",
          "Réponse sous 24h ouvrées",
          "Vos données restent confidentielles",
        ].map((t) => (
          <div key={t} className="flex items-center gap-2 text-white/75 text-sm">
            <Check className="h-4 w-4 text-[#10B981] flex-shrink-0" strokeWidth={3} />
            <span>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IdentityStep({ form, set, setError, inputRef }) {
  return (
    <>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-3">
        Comment vous appelez-vous ?
      </h2>
      <div className="h-6" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          ref={inputRef}
          data-testid="pac-input-first_name"
          placeholder="Prénom"
          value={form.first_name}
          onChange={(e) => { set("first_name", e.target.value); setError(""); }}
          className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-lg font-medium text-white placeholder:text-white/30 outline-none transition"
        />
        <input
          data-testid="pac-input-last_name"
          placeholder="Nom"
          value={form.last_name}
          onChange={(e) => { set("last_name", e.target.value); setError(""); }}
          className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-lg font-medium text-white placeholder:text-white/30 outline-none transition"
        />
      </div>
    </>
  );
}

function ContactStep({ form, set, setError, inputRef }) {
  return (
    <>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-3">
        Comment vous joindre ?
      </h2>
      <p className="flex items-start gap-2 text-white/60 text-sm leading-relaxed mb-8">
        <Info className="h-4 w-4 mt-0.5 text-[#0055A4] flex-shrink-0" />
        <span>Un conseiller ECONATION vous rappelle sous 24h ouvrées.</span>
      </p>
      <div className="grid grid-cols-1 gap-4">
        <input
          ref={inputRef}
          data-testid="pac-input-phone"
          type="tel" inputMode="tel"
          placeholder="Téléphone (ex : 06 12 34 56 78)"
          value={form.phone}
          onChange={(e) => { set("phone", e.target.value); setError(""); }}
          className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-lg font-medium text-white placeholder:text-white/30 outline-none transition"
        />
        <input
          data-testid="pac-input-email"
          type="email" inputMode="email" autoComplete="email"
          placeholder="Email"
          value={form.email}
          onChange={(e) => { set("email", e.target.value); setError(""); }}
          className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-lg font-medium text-white placeholder:text-white/30 outline-none transition"
        />
      </div>
    </>
  );
}

function AddressStep({ form, set, setError, inputRef }) {
  return (
    <>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-3">
        Adresse exacte des travaux
      </h2>
      <div className="h-6" />
      <div className="grid grid-cols-1 gap-4">
        <input
          ref={inputRef}
          data-testid="pac-input-work_address"
          placeholder="Numéro et rue (ex : 12 rue de la République)"
          value={form.work_address}
          onChange={(e) => { set("work_address", e.target.value); setError(""); }}
          className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-lg font-medium text-white placeholder:text-white/30 outline-none transition"
        />
        <div className="grid grid-cols-3 gap-4">
          <input
            data-testid="pac-input-postal_code"
            inputMode="numeric" maxLength={5}
            placeholder="Code postal"
            value={form.postal_code}
            onChange={(e) => { set("postal_code", e.target.value.replace(/\D/g, "")); setError(""); }}
            className="col-span-1 w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-lg font-medium text-white placeholder:text-white/30 outline-none transition"
          />
          <input
            data-testid="pac-input-city"
            placeholder="Ville"
            value={form.city}
            onChange={(e) => { set("city", e.target.value); setError(""); }}
            className="col-span-2 w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-2xl px-6 py-5 text-lg font-medium text-white placeholder:text-white/30 outline-none transition"
          />
        </div>
      </div>
    </>
  );
}

function AtticDetails({ form, set, setError }) {
  return (
    <div className="space-y-6">
      <SubQuestion
        label="Vos combles sont-ils isolés ?"
        options={[{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }]}
        value={form.attic_insulated}
        onChange={(v) => { set("attic_insulated", v); setError(""); }}
        idPrefix="attic_insulated"
      />
      {form.attic_insulated === "yes" && (
        <>
          <SubText
            label="En quelle année ont-ils été isolés ?"
            placeholder="Ex : 2018"
            value={form.attic_insulation_year}
            onChange={(v) => { set("attic_insulation_year", v.replace(/\D/g, "").slice(0,4)); setError(""); }}
            testid="attic_insulation_year"
          />
          <SubQuestion
            label="Isolation réalisée dans le cadre d'une offre à 1 € ?"
            options={[{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }, { v: "unknown", l: "Je ne sais pas" }]}
            value={form.attic_one_euro_offer}
            onChange={(v) => { set("attic_one_euro_offer", v); setError(""); }}
            idPrefix="attic_one_euro_offer"
          />
        </>
      )}
    </div>
  );
}

function BasementDetails({ form, set, setError }) {
  return (
    <div className="space-y-6">
      <SubQuestion
        label="Votre sous-sol est-il isolé ?"
        options={[{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }]}
        value={form.basement_insulated}
        onChange={(v) => { set("basement_insulated", v); setError(""); }}
        idPrefix="basement_insulated"
      />
      {form.basement_insulated === "yes" && (
        <>
          <SubText
            label="En quelle année a-t-il été isolé ?"
            placeholder="Ex : 2020"
            value={form.basement_insulation_year}
            onChange={(v) => { set("basement_insulation_year", v.replace(/\D/g, "").slice(0,4)); setError(""); }}
            testid="basement_insulation_year"
          />
          <SubQuestion
            label="Isolation réalisée dans le cadre d'une offre à 1 € ?"
            options={[{ v: "yes", l: "Oui" }, { v: "no", l: "Non" }, { v: "unknown", l: "Je ne sais pas" }]}
            value={form.basement_one_euro_offer}
            onChange={(v) => { set("basement_one_euro_offer", v); setError(""); }}
            idPrefix="basement_one_euro_offer"
          />
        </>
      )}
    </div>
  );
}

function SubQuestion({ label, options, value, onChange, idPrefix }) {
  return (
    <div>
      <div className="text-white/85 text-base font-medium mb-3">{label}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {options.map((opt) => (
          <button
            key={opt.v}
            data-testid={`pac-sub-${idPrefix}-${opt.v}`}
            onClick={() => onChange(opt.v)}
            className={`rounded-xl border-2 py-3 px-4 text-sm font-semibold transition ${
              value === opt.v
                ? "border-[#0055A4] bg-[#0055A4]/10 text-white"
                : "border-white/10 bg-white/[0.03] text-white/80 hover:border-white/25"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function SubText({ label, placeholder, value, onChange, testid }) {
  return (
    <div>
      <div className="text-white/85 text-base font-medium mb-3">{label}</div>
      <input
        data-testid={`pac-sub-${testid}`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="numeric"
        maxLength={4}
        className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#0055A4] rounded-xl px-5 py-4 text-lg font-semibold text-white placeholder:text-white/25 outline-none transition"
      />
    </div>
  );
}

function ConsentStep({ form, set }) {
  return (
    <div>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-6">
        Dernière étape : validation & envoi
      </h2>
      <p className="text-white/70 text-base leading-relaxed mb-6">
        Votre dossier va être transmis à nos experts en partenariat avec <strong className="text-white">Rénobat</strong>.
        Nous vous recontacterons sous 24h ouvrées avec l&apos;estimation détaillée des aides.
      </p>
      <label className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 cursor-pointer hover:border-white/20 transition">
        <input
          type="checkbox"
          data-testid="pac-input-gdpr_consent"
          checked={form.gdpr_consent}
          onChange={(e) => set("gdpr_consent", e.target.checked)}
          className="mt-1 h-5 w-5 rounded border-white/30 bg-white/10 text-[#0055A4] focus:ring-[#0055A4]"
        />
        <span className="text-white/80 text-sm leading-relaxed">
          J&apos;accepte que mes données soient traitées par <strong className="text-white">ECONATION</strong> et
          son partenaire <strong className="text-white">Rénobat</strong> dans le cadre de l&apos;étude
          de mon éligibilité aux aides CEE et MaPrimeRénov&apos;. Mes données ne seront pas
          transmises à des tiers non partenaires et je peux demander leur suppression à tout moment.
        </span>
      </label>
    </div>
  );
}

/* ================= SUCCESS SCREEN ================= */

function SuccessScreen({ firstName, result, householdComposition, onClose }) {
  const styles = {
    grand_precaire: { emoji: "🔵", grad: "from-[#3B82F6] to-[#2563EB]", text: "Grand précaire" },
    precaire: { emoji: "🟡", grad: "from-[#EAB308] to-[#CA8A04]", text: "Précaire" },
    classique: { emoji: "🌸", grad: "from-[#EC4899] to-[#DB2777]", text: "Classique" },
  };
  const st = result ? styles[result.category] : null;
  const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

  return (
    <div data-testid="pac-wizard-success" className="text-center animate-[fadein_.6s_ease-out]">
      <div className="mx-auto mb-8 h-20 w-20 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] grid place-items-center shadow-[0_20px_60px_-10px_rgba(16,185,129,0.4)]">
        <Check className="h-9 w-9 text-white" strokeWidth={3} />
      </div>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-6">
        Merci{firstName ? ` ${firstName}` : ""},<br/>votre demande est enregistrée.
      </h2>

      {result?.dossier_number && (
        <div className="mx-auto mb-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4" data-testid="pac-dossier-number">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 font-semibold mb-1">Votre numéro de dossier</div>
          <div className="font-display text-2xl sm:text-3xl font-bold tracking-wider text-white">{result.dossier_number}</div>
          <div className="text-white/50 text-[11px] mt-1">À conserver pour toute correspondance</div>
        </div>
      )}

      {result && st && (
        <div data-testid="pac-wizard-classification" className="max-w-md mx-auto mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 font-semibold mb-3">Profil identifié</div>
          <div className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${st.grad} px-4 py-2 mb-5`}>
            <span className="text-lg leading-none">{st.emoji}</span>
            <span className="text-white text-sm font-bold tracking-wide">{st.text}</span>
          </div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-white/50 font-semibold mb-2">Aides estimées</div>
          <div className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {fmt(result.estimated_aids_min)}€ – {fmt(result.estimated_aids_max)}€
          </div>
          <div className="text-white/50 text-xs mt-2">MaPrimeRénov&apos; + prime CEE cumulées</div>
        </div>
      )}

      {/* Documents upload */}
      {result?.id && <DocumentsUploader leadId={result.id} householdComposition={householdComposition} />}

      <p className="text-white/60 text-sm sm:text-base leading-relaxed max-w-lg mx-auto mt-10 mb-8">
        Nos experts <strong className="text-white">ECONATION × Rénobat</strong> vous recontactent
        sous <strong className="text-[#0055A4]">24h ouvrées</strong>.
      </p>
      <button
        onClick={onClose}
        data-testid="pac-wizard-success-close"
        className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 px-8 py-3.5 text-white text-sm font-semibold tracking-wide transition"
      >
        Retour au site
      </button>
    </div>
  );
}

/* ================= DOCUMENTS UPLOADER ================= */

const DOC_CONFIG = [
  { key: "avis_imposition", label: "Avis d'imposition 2024 ou 2025", icon: FileText, hint: "PDF ou photo lisible" },
  { key: "cni_recto_mr", label: "CNI Mr — recto", icon: CreditCard, hint: "Recto de la carte d'identité de Monsieur" },
  { key: "cni_verso_mr", label: "CNI Mr — verso", icon: CreditCard, hint: "Verso de la carte d'identité de Monsieur" },
  { key: "cni_recto_mme", label: "CNI Mme — recto", icon: CreditCard, hint: "Recto de la carte d'identité de Madame" },
  { key: "cni_verso_mme", label: "CNI Mme — verso", icon: CreditCard, hint: "Verso de la carte d'identité de Madame" },
  { key: "justif_domicile", label: "Justificatif de domicile", icon: MapPin, hint: "Facture énergie / eau < 3 mois (pas de facture mobile)" },
];

function DocumentsUploader({ leadId, householdComposition }) {
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState("");

  const docs = householdComposition === "single"
    ? DOC_CONFIG.filter((d) => !d.key.endsWith("_mme"))
    : DOC_CONFIG;
  const total = docs.length;
  const selectedCount = Object.values(files).filter(Boolean).length;

  const onFile = (key, file) => {
    if (!file) { setFiles((f) => { const c = { ...f }; delete c[key]; return c; }); return; }
    if (file.size > 5 * 1024 * 1024) { setError(`Le fichier "${file.name}" dépasse 5 MB.`); return; }
    const ok = /(pdf|jpe?g|png|webp|heic|heif)$/i.test(file.name) || /^(application\/pdf|image\/)/.test(file.type);
    if (!ok) { setError(`Format non supporté (PDF, JPG, PNG uniquement) pour "${file.name}".`); return; }
    setError("");
    setFiles((f) => ({ ...f, [key]: file }));
  };

  const submit = async () => {
    if (selectedCount === 0) { setError("Sélectionnez au moins un document."); return; }
    setUploading(true); setError("");
    try {
      await uploadParticulierDocuments(leadId, files);
      setUploaded(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Envoi des documents échoué. Réessayez.";
      setError(typeof msg === "string" ? msg : "Envoi des documents échoué.");
    } finally {
      setUploading(false);
    }
  };

  if (uploaded) {
    return (
      <div data-testid="pac-docs-success" className="max-w-lg mx-auto rounded-2xl border border-[#10B981]/30 bg-[#10B981]/10 p-6">
        <div className="flex items-center gap-3 justify-center">
          <div className="h-10 w-10 rounded-full bg-[#10B981] grid place-items-center flex-shrink-0">
            <Check className="h-5 w-5 text-white" strokeWidth={3} />
          </div>
          <div className="text-left">
            <div className="text-white font-semibold text-sm">Documents transmis en toute sécurité</div>
            <div className="text-white/70 text-xs mt-0.5">Vos documents ont été envoyés à notre équipe. Nous vous recontactons rapidement.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pac-docs-uploader" className="max-w-lg mx-auto mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7 text-left">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-9 w-9 rounded-lg bg-[#0055A4]/15 text-[#0055A4] grid place-items-center flex-shrink-0">
          <Upload className="h-4 w-4" />
        </div>
        <div>
          <div className="text-white font-semibold text-sm mb-1">Accélérez votre dossier (facultatif)</div>
          <div className="text-white/60 text-xs leading-relaxed">
            Transmettez vos pièces dès maintenant — envoi chiffré HTTPS, réception directe par notre équipe.
          </div>
        </div>
      </div>

      <div className="space-y-2.5 mb-5">
        {docs.map((d) => {
          const Ic = d.icon;
          const selected = files[d.key];
          return (
            <label
              key={d.key}
              data-testid={`pac-doc-${d.key}`}
              className={`flex items-center gap-3 rounded-xl border-2 border-dashed p-3.5 cursor-pointer transition ${
                selected ? "border-[#10B981]/50 bg-[#10B981]/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/25"
              }`}
            >
              <div className={`h-9 w-9 rounded-lg grid place-items-center flex-shrink-0 ${selected ? "bg-[#10B981]/20 text-[#10B981]" : "bg-white/[0.05] text-white/50"}`}>
                {selected ? <Check className="h-4 w-4" strokeWidth={3} /> : <Ic className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">{d.label}</div>
                <div className="text-white/50 text-[11px] truncate">
                  {selected ? `${selected.name} · ${(selected.size/1024).toFixed(0)} KB` : d.hint}
                </div>
              </div>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
                className="hidden"
                data-testid={`pac-doc-input-${d.key}`}
                onChange={(e) => onFile(d.key, e.target.files?.[0] || null)}
              />
              <div className="text-white/40 text-xs uppercase tracking-wide flex-shrink-0">
                {selected ? "Remplacer" : "Choisir"}
              </div>
            </label>
          );
        })}
      </div>

      {error && <div className="text-[#FCA5A5] text-xs mb-3" data-testid="pac-docs-error">{error}</div>}

      <RgpdProtectionNotice />

      <button
        onClick={submit}
        disabled={uploading || selectedCount === 0}
        data-testid="pac-docs-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#0055A4] to-[#003D7A] hover:from-[#003D7A] hover:to-[#0055A4] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3.5 text-white text-sm font-semibold tracking-wide transition shadow-lg shadow-[#0055A4]/30"
      >
        {uploading ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours...</>
        ) : (
          <><Upload className="h-4 w-4" /> Envoyer {selectedCount > 0 ? `${selectedCount}/${total} document(s)` : "les documents"}</>
        )}
      </button>

      <div className="mt-4 flex items-center gap-2 text-white/40 text-[10px]">
        <ShieldCheck className="h-3 w-3 text-[#10B981]" />
        <span>Transfert chiffré HTTPS · 5 MB max par fichier · PDF, JPG, PNG</span>
      </div>
    </div>
  );
}


/* ================= RGPD PROTECTION NOTICE ================= */

export function RgpdProtectionNotice() {
  return (
    <div
      data-testid="rgpd-protection-notice"
      className="mb-5 rounded-2xl border border-[#10B981]/25 bg-gradient-to-br from-[#10B981]/[0.06] to-[#059669]/[0.03] p-5"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-8 w-8 rounded-lg bg-[#10B981]/15 text-[#10B981] grid place-items-center flex-shrink-0">
          <Lock className="h-4 w-4" />
        </div>
        <div className="text-white text-sm font-semibold tracking-tight">Protection de vos données</div>
      </div>
      <div className="text-white/70 text-[12.5px] leading-[1.7] space-y-2.5">
        <p>
          Les documents que vous déposez sont transmis via une <strong className="text-white">connexion sécurisée</strong> et
          utilisés exclusivement pour l&apos;étude de votre dossier d&apos;aides CEE et MaPrimeRénov&apos;.
        </p>
        <p>
          Afin de lutter contre la <strong className="text-white">fraude aux aides publiques</strong>, votre dossier
          peut faire l&apos;objet de contrôles de cohérence et de conformité.
        </p>
        <p>
          Conformément au <strong className="text-white">Règlement Général sur la Protection des Données (RGPD)</strong>,
          vos informations sont traitées de manière confidentielle et ne sont conservées que pendant la durée
          strictement nécessaire à l&apos;instruction de votre dossier.
        </p>
        <p>
          En cas de dossier non poursuivi, vos documents sont automatiquement supprimés de nos espaces de traitement
          dans un délai maximal de <strong className="text-white">30 jours</strong>, sauf obligation légale de conservation.
        </p>
      </div>
      <div className="mt-4 flex items-start gap-2 rounded-lg bg-black/20 border border-white/5 px-3 py-2.5">
        <Lock className="h-3.5 w-3.5 text-[#10B981] mt-0.5 flex-shrink-0" />
        <span className="text-white/60 text-[11.5px] leading-relaxed">
          Toutes les données sont hébergées sur des <strong className="text-white/85">serveurs sécurisés</strong> et
          ne sont accessibles qu&apos;aux personnes habilitées à traiter votre dossier.
        </span>
      </div>
    </div>
  );
}
